import { Container, Graphics } from 'pixi.js';
import {
  TRAIL_SPAWN_INTERVAL,
  TRAIL_LIFETIME,
  TRAIL_LIFETIME_BOOST,
  TRAIL_SIZE_MULTIPLIER,
  TRAIL_SIZE_MULTIPLIER_BOOST,
  TRAIL_MAX_SEGMENTS,
  BOOST_PARTICLE_RATE,
  BOOST_PARTICLE_SPEED_MIN,
  BOOST_PARTICLE_SPEED_MAX,
  BOOST_PARTICLE_SIZE,
  BOOST_PARTICLE_LIFETIME,
  BOOST_SPEED_LINE_COUNT,
  BOOST_SPEED_LINE_LENGTH_MIN,
  BOOST_SPEED_LINE_LENGTH_MAX,
} from '../../constants';
import { EyeProtectionSettings, EyeProtectionLevel } from '../../ui/EyeProtectionToggle';

/**
 * Individual trail segment data
 */
interface TrailSegment {
  /** Graphics object */
  graphic: Graphics;
  /** X position */
  x: number;
  /** Y position */
  y: number;
  /** Initial radius */
  radius: number;
  /** Color */
  color: number;
  /** Remaining lifetime */
  lifetime: number;
  /** Max lifetime for progress calc */
  maxLifetime: number;
  /** Is this a boost trail (different visuals) */
  isBoosting: boolean;
  /** Whether segment is active */
  active: boolean;
}

/**
 * Boost particle data
 */
interface BoostParticle {
  /** Graphics object */
  graphic: Graphics;
  /** Position */
  x: number;
  y: number;
  /** Velocity */
  vx: number;
  vy: number;
  /** Size */
  size: number;
  /** Lifetime */
  lifetime: number;
  maxLifetime: number;
  /** Color */
  color: number;
  /** Active */
  active: boolean;
}

/**
 * Speed line data for boost effect
 */
interface SpeedLine {
  /** Graphics object */
  graphic: Graphics;
  /** Position */
  x: number;
  y: number;
  /** Direction angle */
  angle: number;
  /** Length */
  length: number;
  /** Lifetime */
  lifetime: number;
  /** Active */
  active: boolean;
}

/**
 * TrailSystem - Movement trails and boost effects
 *
 * Features:
 * - Fading trail segments behind worms
 * - Enhanced boost trails (thicker, longer lasting)
 * - Boost speed particles
 * - Speed lines effect during boost
 */
export class TrailSystem {
  /** Main container */
  public container: Container;

  /** Trail segments */
  private trails: TrailSegment[] = [];
  private trailPool: TrailSegment[] = [];

  /** Boost particles */
  private boostParticles: BoostParticle[] = [];
  private boostParticlePool: BoostParticle[] = [];

  /** Speed lines */
  private speedLines: SpeedLine[] = [];
  private speedLinePool: SpeedLine[] = [];

  /** Time since last trail spawn per worm */
  private lastTrailTime: Map<string, number> = new Map();

  /** Particle spawn accumulator per worm */
  private particleAccumulator: Map<string, number> = new Map();

  constructor() {
    this.container = new Container();
    this.container.label = 'trail-system';

    // Pre-allocate pools
    this.initializePools();
  }

  /**
   * Initialize object pools
   */
  private initializePools(): void {
    // Trail pool
    for (let i = 0; i < TRAIL_MAX_SEGMENTS; i++) {
      const graphic = new Graphics();
      graphic.visible = false;
      this.container.addChild(graphic);
      this.trailPool.push({
        graphic,
        x: 0,
        y: 0,
        radius: 10,
        color: 0xffffff,
        lifetime: 0,
        maxLifetime: TRAIL_LIFETIME,
        isBoosting: false,
        active: false,
      });
    }

    // Boost particle pool
    for (let i = 0; i < 50; i++) {
      const graphic = new Graphics();
      graphic.visible = false;
      this.container.addChild(graphic);
      this.boostParticlePool.push({
        graphic,
        x: 0,
        y: 0,
        vx: 0,
        vy: 0,
        size: BOOST_PARTICLE_SIZE,
        lifetime: 0,
        maxLifetime: BOOST_PARTICLE_LIFETIME,
        color: 0xffffff,
        active: false,
      });
    }

    // Speed line pool
    for (let i = 0; i < 20; i++) {
      const graphic = new Graphics();
      graphic.visible = false;
      this.container.addChild(graphic);
      this.speedLinePool.push({
        graphic,
        x: 0,
        y: 0,
        angle: 0,
        length: 30,
        lifetime: 0,
        active: false,
      });
    }
  }

  /**
   * Get a trail segment from the pool
   */
  private getTrailSegment(): TrailSegment | null {
    for (const seg of this.trailPool) {
      if (!seg.active) return seg;
    }
    // Recycle oldest
    if (this.trails.length > 0) {
      const oldest = this.trails.shift();
      if (oldest) {
        oldest.active = false;
        oldest.graphic.visible = false;
        return oldest;
      }
    }
    return null;
  }

  /**
   * Get a boost particle from the pool
   */
  private getBoostParticle(): BoostParticle | null {
    for (const p of this.boostParticlePool) {
      if (!p.active) return p;
    }
    return null;
  }

  /**
   * Get a speed line from the pool
   */
  private getSpeedLine(): SpeedLine | null {
    for (const l of this.speedLinePool) {
      if (!l.active) return l;
    }
    return null;
  }

  /**
   * Add a trail point for a worm
   */
  addTrailPoint(
    wormId: string,
    x: number,
    y: number,
    radius: number,
    color: number,
    boosting: boolean,
    angle: number,
    dt: number
  ): void {
    const protectionLevel = EyeProtectionSettings.getInstance().getLevel();

    // Skip trail effects completely for strong eye protection
    if (protectionLevel === EyeProtectionLevel.STRONG) {
      return;
    }

    // Check spawn interval (doubled for mild protection - less frequent trails)
    const spawnInterval = protectionLevel === EyeProtectionLevel.MILD
      ? TRAIL_SPAWN_INTERVAL * 2
      : TRAIL_SPAWN_INTERVAL;

    const lastTime = this.lastTrailTime.get(wormId) ?? 0;
    const newTime = lastTime + dt;
    this.lastTrailTime.set(wormId, newTime);

    if (newTime < spawnInterval) return;
    this.lastTrailTime.set(wormId, 0);

    // Spawn trail segment
    const seg = this.getTrailSegment();
    if (seg) {
      const sizeMultiplier = boosting ? TRAIL_SIZE_MULTIPLIER_BOOST : TRAIL_SIZE_MULTIPLIER;
      const lifetime = boosting ? TRAIL_LIFETIME_BOOST : TRAIL_LIFETIME;

      seg.x = x;
      seg.y = y;
      seg.radius = radius * sizeMultiplier;
      seg.color = boosting ? this.brightenColor(color, 0.3) : color;
      seg.lifetime = lifetime;
      seg.maxLifetime = lifetime;
      seg.isBoosting = boosting;
      seg.active = true;
      seg.graphic.visible = true;
      this.trails.push(seg);
      this.drawTrailSegment(seg);
    }

    // Handle boost particles
    if (boosting) {
      const accumulator = (this.particleAccumulator.get(wormId) ?? 0) + dt;
      const particleInterval = 1 / BOOST_PARTICLE_RATE;

      if (accumulator >= particleInterval) {
        this.particleAccumulator.set(wormId, accumulator - particleInterval);
        this.spawnBoostParticle(x, y, angle, color, radius);
      } else {
        this.particleAccumulator.set(wormId, accumulator);
      }
    }
  }

  /**
   * Spawn speed lines effect for boost
   */
  spawnSpeedLines(x: number, y: number, angle: number, color: number): void {
    const protectionLevel = EyeProtectionSettings.getInstance().getLevel();

    // Skip speed lines completely for strong eye protection
    if (protectionLevel === EyeProtectionLevel.STRONG) {
      return;
    }

    // Reduce speed line count for mild protection
    const lineCount = protectionLevel === EyeProtectionLevel.MILD
      ? Math.ceil(BOOST_SPEED_LINE_COUNT / 2)
      : BOOST_SPEED_LINE_COUNT;

    for (let i = 0; i < lineCount; i++) {
      const line = this.getSpeedLine();
      if (!line) continue;

      // Spawn around the worm, facing backward
      const spreadAngle = (Math.random() - 0.5) * Math.PI * 0.6;
      const backAngle = angle + Math.PI + spreadAngle;
      const spawnDist = 30 + Math.random() * 40;

      line.x = x + Math.cos(backAngle) * spawnDist;
      line.y = y + Math.sin(backAngle) * spawnDist;
      line.angle = backAngle;
      line.length = BOOST_SPEED_LINE_LENGTH_MIN +
        Math.random() * (BOOST_SPEED_LINE_LENGTH_MAX - BOOST_SPEED_LINE_LENGTH_MIN);
      line.lifetime = 0.15 + Math.random() * 0.1;
      line.active = true;
      line.graphic.visible = true;
      this.speedLines.push(line);
      this.drawSpeedLine(line, color);
    }
  }

  /**
   * Spawn a boost particle
   */
  private spawnBoostParticle(x: number, y: number, angle: number, color: number, radius: number): void {
    const particle = this.getBoostParticle();
    if (!particle) return;

    // Spawn behind the worm with some spread
    const backAngle = angle + Math.PI + (Math.random() - 0.5) * 0.8;
    const spawnDist = radius * 0.5;

    particle.x = x + Math.cos(backAngle) * spawnDist;
    particle.y = y + Math.sin(backAngle) * spawnDist;

    // Velocity pointing backward
    const speed = BOOST_PARTICLE_SPEED_MIN +
      Math.random() * (BOOST_PARTICLE_SPEED_MAX - BOOST_PARTICLE_SPEED_MIN);
    particle.vx = Math.cos(backAngle) * speed;
    particle.vy = Math.sin(backAngle) * speed;

    particle.size = BOOST_PARTICLE_SIZE + Math.random() * 2;
    particle.lifetime = BOOST_PARTICLE_LIFETIME;
    particle.maxLifetime = BOOST_PARTICLE_LIFETIME;
    particle.color = this.brightenColor(color, 0.5);
    particle.active = true;
    particle.graphic.visible = true;

    this.boostParticles.push(particle);
    this.drawBoostParticle(particle);
  }

  /**
   * Draw a trail segment with arcade-style glow
   */
  private drawTrailSegment(seg: TrailSegment): void {
    const progress = 1 - seg.lifetime / seg.maxLifetime;
    const alpha = (1 - progress) * 0.7;
    const scale = 1 - progress * 0.4;
    const radius = seg.radius * scale;

    const g = seg.graphic;
    g.clear();

    if (seg.isBoosting) {
      // Boosting trail: vibrant arcade glow with multiple layers
      // Outer glow ring
      g.circle(0, 0, radius * 1.8);
      g.fill({ color: 0xffffff, alpha: alpha * 0.15 });
      // Mid glow
      g.circle(0, 0, radius * 1.4);
      g.fill({ color: seg.color, alpha: alpha * 0.3 });
      // Bright center
      g.circle(0, 0, radius * 1.1);
      g.fill({ color: 0xffffff, alpha: alpha * 0.5 });
      // Core
      g.circle(0, 0, radius * 0.8);
      g.fill({ color: seg.color, alpha });
    } else {
      // Normal trail: smooth fading circle with slight glow
      g.circle(0, 0, radius * 1.2);
      g.fill({ color: seg.color, alpha: alpha * 0.3 });
      g.circle(0, 0, radius);
      g.fill({ color: seg.color, alpha });
    }

    g.position.set(seg.x, seg.y);
  }

  /**
   * Draw a boost particle with bright arcade glow
   */
  private drawBoostParticle(particle: BoostParticle): void {
    const progress = 1 - particle.lifetime / particle.maxLifetime;
    const alpha = (1 - progress) * 0.9;
    const size = particle.size * (1 - progress * 0.3);

    const g = particle.graphic;
    g.clear();
    // Outer glow
    g.circle(0, 0, size * 1.5);
    g.fill({ color: particle.color, alpha: alpha * 0.3 });
    // Bright center
    g.circle(0, 0, size);
    g.fill({ color: 0xffffff, alpha: alpha * 0.8 });
    // Core color
    g.circle(0, 0, size * 0.6);
    g.fill({ color: particle.color, alpha });
    g.position.set(particle.x, particle.y);
  }

  /**
   * Draw a speed line with arcade-style glow
   */
  private drawSpeedLine(line: SpeedLine, color: number): void {
    const g = line.graphic;
    g.clear();

    const alpha = 0.8;
    const endX = Math.cos(line.angle) * line.length;
    const endY = Math.sin(line.angle) * line.length;

    // Wide glow line first (underneath)
    g.moveTo(0, 0);
    g.lineTo(endX, endY);
    g.stroke({ color, width: 6, alpha: alpha * 0.3 });

    // Bright core line
    g.moveTo(0, 0);
    g.lineTo(endX, endY);
    g.stroke({ color: 0xffffff, width: 2, alpha });

    g.position.set(line.x, line.y);
  }

  /**
   * Brighten a color
   */
  private brightenColor(color: number, amount: number): number {
    let r = (color >> 16) & 0xff;
    let g = (color >> 8) & 0xff;
    let b = color & 0xff;

    r = Math.min(255, Math.floor(r + (255 - r) * amount));
    g = Math.min(255, Math.floor(g + (255 - g) * amount));
    b = Math.min(255, Math.floor(b + (255 - b) * amount));

    return (r << 16) | (g << 8) | b;
  }

  /**
   * Update all trail effects
   */
  update(dt: number): void {
    // Update trails
    for (let i = this.trails.length - 1; i >= 0; i--) {
      const seg = this.trails[i];
      if (!seg || !seg.active) continue;

      seg.lifetime -= dt;
      if (seg.lifetime <= 0) {
        seg.active = false;
        seg.graphic.visible = false;
        this.trails.splice(i, 1);
        continue;
      }

      this.drawTrailSegment(seg);
    }

    // Update boost particles
    for (let i = this.boostParticles.length - 1; i >= 0; i--) {
      const p = this.boostParticles[i];
      if (!p || !p.active) continue;

      p.lifetime -= dt;
      if (p.lifetime <= 0) {
        p.active = false;
        p.graphic.visible = false;
        this.boostParticles.splice(i, 1);
        continue;
      }

      // Move particle
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      // Slow down
      p.vx *= 0.95;
      p.vy *= 0.95;

      this.drawBoostParticle(p);
    }

    // Update speed lines
    for (let i = this.speedLines.length - 1; i >= 0; i--) {
      const line = this.speedLines[i];
      if (!line || !line.active) continue;

      line.lifetime -= dt;
      if (line.lifetime <= 0) {
        line.active = false;
        line.graphic.visible = false;
        this.speedLines.splice(i, 1);
        continue;
      }

      // Fade and shrink
      const progress = line.lifetime / 0.2;
      line.graphic.alpha = progress;
      line.graphic.scale.set(progress);
    }
  }

  /**
   * Clear all trails for a specific worm
   */
  clearWormTrail(wormId: string): void {
    this.lastTrailTime.delete(wormId);
    this.particleAccumulator.delete(wormId);
  }

  /**
   * Clear all effects
   */
  clear(): void {
    for (const seg of this.trails) {
      seg.active = false;
      seg.graphic.visible = false;
    }
    this.trails.length = 0;

    for (const p of this.boostParticles) {
      p.active = false;
      p.graphic.visible = false;
    }
    this.boostParticles.length = 0;

    for (const l of this.speedLines) {
      l.active = false;
      l.graphic.visible = false;
    }
    this.speedLines.length = 0;

    this.lastTrailTime.clear();
    this.particleAccumulator.clear();
  }

  /**
   * Destroy the system
   */
  destroy(): void {
    this.clear();
    for (const seg of this.trailPool) {
      seg.graphic.destroy();
    }
    for (const p of this.boostParticlePool) {
      p.graphic.destroy();
    }
    for (const l of this.speedLinePool) {
      l.graphic.destroy();
    }
    this.trailPool.length = 0;
    this.boostParticlePool.length = 0;
    this.speedLinePool.length = 0;
    this.container.destroy({ children: true });
  }
}
